// client/src/components/bank/SubmitBid.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { parseCurrencyInput, handleCurrencyInputChange, formatDuration, formatCurrencyWithSymbol } from '../utils/formatUtils';
import { sanitizeInput } from '../utils/sanitize';
import { VAT_CREDIT_DAY_COUNT_BASES as DAY_COUNT_BASES } from '../vatCredit/vatCreditUtils';
import { Edit } from 'lucide-react';

const SubmitBid = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
    
  const [request, setRequest] = useState(null);
  const [isAffiliatedBank, setIsAffiliatedBank] = useState(false); // NEW
  const [formData, setFormData] = useState({
    amount: '',
    interestRate: '',
    rateType: '',
    baseRate: '',
    dayCountBasis: 'act_360', // Default ACT/360, la banca lo cambia solo se serve
    noticePeriod: '',
    //lockInPeriod: '',
    penaltyApplies: '',
    penaltyDescription: '',
    supportAccount: '', // NEW
    notes: ''
  });
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [bidSubmitted, setBidSubmitted] = useState(false);

  // Helper function to parse interest rate supporting both comma and dot
  const parseInterestRate = (value) => {
    if (!value || typeof value !== 'string') return 0;
    
    // Replace comma with dot and parse as float
    const normalizedValue = value.trim().replace(',', '.');
    const parsed = parseFloat(normalizedValue);
    
    return isNaN(parsed) ? 0 : parsed;
  };
    
  // Helper function to get label text based on rate type
  const getInterestRateLabel = (rateType) => {
    return rateType === 'floating' ? t('bids.interestRateFloating') : t('bids.interestRateFixed');
  };

  // Helper function to get base rate options
const getBaseRateOptions = (currency) => {
  const ratesByCurrency = {
    'EUR': [
      { value: 'euribor_1w', label: t('bids.euribor1Week') },
      { value: 'euribor_1m', label: t('bids.euribor1Month') },
      { value: 'euribor_3m', label: t('bids.euribor3Months') },
      { value: 'euribor_6m', label: t('bids.euribor6Months') },
      { value: 'euribor_12m', label: t('bids.euribor12Months') },
      { value: 'estr', label: t('bids.estr') },
      { value: 'bce_dfr', label: t('bids.bceDfr') }
    ],
    'USD': [
      { value: 'sofr_1m', label: t('bids.sofr1Month') },
      { value: 'sofr_3m', label: t('bids.sofr3Months') },
      { value: 'sofr_6m', label: t('bids.sofr6Months') },
      { value: 'sofr_12m', label: t('bids.sofr12Months') },
      { value: 'effr', label: t('bids.effr') }
    ],
    'GBP': [
      { value: 'sonia_1m', label: t('bids.sonia1Month') },
      { value: 'sonia_3m', label: t('bids.sonia3Months') },
      { value: 'sonia_6m', label: t('bids.sonia6Months') },
      { value: 'sonia_12m', label: t('bids.sonia12Months') }
    ],
    'CHF': [
      { value: 'saron_1m', label: t('bids.saron1Month') },
      { value: 'saron_2m', label: t('bids.saron2Months') },
      { value: 'saron_3m', label: t('bids.saron3Months') },
      { value: 'saron_6m', label: t('bids.saron6Months') },
      { value: 'saron_12m', label: t('bids.saron12Months') }
    ],
    'AUD': [
      { value: 'aonia', label: t('bids.aonia') },
      { value: 'rba_cash_rate', label: t('bids.rbaCashRate') },
      { value: 'bbsy_1m', label: t('bids.bbsy1Month') },
      { value: 'bbsy_2m', label: t('bids.bbsy2Months') },
      { value: 'bbsy_3m', label: t('bids.bbsy3Months') },
      { value: 'bbsy_4m', label: t('bids.bbsy4Months') },
      { value: 'bbsy_5m', label: t('bids.bbsy5Months') },
      { value: 'bbsy_6m', label: t('bids.bbsy6Months') }
    ],
    'NZD': [
      { value: 'nzonia', label: t('bids.nzonia') },
      { value: 'rnz_ocr', label: t('bids.rnzOcr') },
      { value: 'bkbm1Month', label: t('bids.bkbm1Month') },
      { value: 'bkbm3Months', label: t('bids.bkbm3Months') },
      { value: 'bkbm6Months', label: t('bids.bkbm6Months') }
    ],
    'CAD': [
      { value: 'corra_1m', label: t('bids.corra1Month') },
      { value: 'corra_3m', label: t('bids.corra3Months') },
      { value: 'boc_overnight_rate', label: t('bids.bocOvernightRate') }
    ]
  };
  
  return ratesByCurrency[currency] || ratesByCurrency['EUR'];
};
  
  // Helper function to get withdrawal type display text
  const getWithdrawalTypeDisplay = (withdrawalType) => {
    switch (withdrawalType) {
      case 'demand':
        return t('requests.demand');
      case 'early_allowed':
        return t('requests.earlyWithdrawalAllowed');
      case 'no_withdrawal':
        return t('requests.noWithdrawalAllowed');
      default:
        return 'Unknown';
    }
  };

  // Helper function to determine available rate types
  const getAvailableRateTypes = () => {
    if (!request || !request.rateTypes) {
      return []; // Return empty if no rate types available
    }
    
    return request.rateTypes.map(rateType => ({
      value: rateType,
      label: rateType === 'fixed' ? t('requests.fixed') : t('requests.floating')
    }));
  };

  // Helper function to check if rate type selection is needed
  const needsRateTypeSelection = () => {
    return request && request.rateTypes && request.rateTypes.length > 1;
  };
  
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch request details
        const requestRes = await axios.get(`/api/requests/${id}`);
        setRequest(requestRes.data.request);

        // NEW: Check if current bank user is affiliated
      try {
        const userRes = await axios.get('/api/auth/profile');
        const currentUser = userRes.data.user;
        
        // Se l'utente bancario è un referral, è affiliato
        // (perché può vedere la request SOLO se è stata creata dalla company che l'ha invitato)
        if (currentUser.role === 'bank' && currentUser.isReferral === true) {
          setIsAffiliatedBank(true);
        } else {
          // Banca third party, NON affiliata
          setIsAffiliatedBank(false);
        }
        
      } catch (err) {
        console.error('Error checking bank affiliation');
        setIsAffiliatedBank(false); // Default: mostra il campo
      }

        setLoading(false);
      } catch (err) {
        setError(t('common.errors.fetchRequestDetails'));
        setLoading(false);
      }
    };
    
    fetchData();
  }, [id, t]);
  
const onChange = e => {
  // Handle currency formatting for amount fields
  if (e.target.name === 'amount') {
    handleCurrencyInputChange(e, setFormData, formData, 'amount');
  } else if (e.target.name === 'interestRate') {
    // Allow input with both comma and dot, but sanitize to only allow valid characters
    const rawValue = e.target.value;
    const sanitizedValue = rawValue.replace(/[^0-9.,-]/g, '');
    setFormData({ ...formData, [e.target.name]: sanitizedValue });
  } else {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  }
    
  // Clear error message when user starts typing again
  if (error) {
    setError('');
  }
};
  
  const onSubmit = async e => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    
    // Get numeric value from amount input, regardless of format
    let formattedAmount = parseCurrencyInput(formData.amount);
    
    // Validate amount and interest rate
    if (isNaN(formattedAmount) || formattedAmount <= 0) {
      setError(t('common.errors.validAmount'));
      setSubmitting(false);
      return;
    }

    // Interest Rate Validation - Using the new parsing function
    const interestRateValue = parseInterestRate(formData.interestRate);
      if (isNaN(interestRateValue)) {
      setError(t('common.errors.validInterestRate'));
      setSubmitting(false);
      return;
    }
    
    // Validate notice period for early withdrawal allowed
    if (request.withdrawalType === 'early_allowed') {
      const notice = parseInt(formData.noticePeriod);
      if (isNaN(notice) || notice < 1) {
        setError(t('common.errors.validNoticePeriod'));
        setSubmitting(false);
        return;
      }
      
      // NEW: Validate penalty fields for early withdrawal
      if (!formData.penaltyApplies || (formData.penaltyApplies !== 'yes' && formData.penaltyApplies !== 'no')) {
        setError(t('common.errors.penaltyRequired'));
        setSubmitting(false);
        return;
      }
      
      // If penalty applies, description is required
      if (formData.penaltyApplies === 'yes' && (!formData.penaltyDescription || formData.penaltyDescription.trim() === '')) {
        setError(t('common.errors.penaltyDescRequired'));
        setSubmitting(false);
        return;
      }
    }

     // NEW: Validate support account selection - ONLY if NOT affiliated
     if (!isAffiliatedBank) {
      if (!formData.supportAccount || (formData.supportAccount !== 'yes' && formData.supportAccount !== 'no')) {
        setError(t('common.errors.supportAccountRequired'));
        setSubmitting(false);
        return;
      }
    }

    // NEW: Validate rate type selection if multiple types are available
    if (needsRateTypeSelection() && !formData.rateType) {
      setError(t('common.errors.selectRateType'));
      setSubmitting(false);
      return;
    }
    
    // Determine the rate type to use
    const selectedRateType = needsRateTypeSelection() 
      ? formData.rateType 
      : (request.rateTypes && request.rateTypes.length === 1 ? request.rateTypes[0] : request.rateType);

      if (selectedRateType === 'floating') {
      if (!formData.baseRate) {
        setError(t('common.errors.selectBaseRate'));
        setSubmitting(false);
        return;
      }
}

    /* Validate lock-in period for overnight requests
    if (request.duration === 'overnight') {
      if (!formData.lockInPeriod) {
        setError(t('common.errors.lockInRequired'));
        setSubmitting(false);
        return;
      }
      
      // Validate that the date is in the future
      const selectedDate = new Date(formData.lockInPeriod);
      const today = new Date();
      today.setHours(0, 0, 0, 0); // Reset time to compare only dates
      
      if (selectedDate <= today) {
        setError(t('common.errors.lockInFuture'));
        setSubmitting(false);
        return;
      }
    }*/
    
    try {
      const bidData = {
        requestId: request.id,
        amount: formattedAmount,
        interestRate: parseInterestRate(formData.interestRate), // Using the new parsing function
        rateType: selectedRateType,
        baseRate: selectedRateType === 'floating' ? formData.baseRate : null,
        dayCountBasis: formData.dayCountBasis,
        noticePeriod: request.withdrawalType === 'early_allowed' ? parseInt(formData.noticePeriod) : null,
        //lockInPeriod: request.duration === 'overnight' ? formData.lockInPeriod : null,
        // NEW: Penalty fields
        penaltyApplies: request.withdrawalType === 'early_allowed' ? (formData.penaltyApplies === 'yes') : null,
        penaltyDescription: (request.withdrawalType === 'early_allowed' && formData.penaltyApplies === 'yes') ? sanitizeInput(formData.penaltyDescription) : null,
        supportAccount: isAffiliatedBank ? false : (formData.supportAccount === 'yes'),
        notes: sanitizeInput(formData.notes)
      };
      
      await axios.post('/api/bids', bidData);
      
      // Set bid as submitted
      setBidSubmitted(true);
            
      // Redirect to my bids page after a short delay (to let the user see the success message)
      setTimeout(() => {
        const pastBidsPath = isAffiliatedBank
          ? (request.requestType === 'hot_money' ? '/bank/past-bids/hot-money' : '/bank/past-bids/allocation')
          : '/bank/past-bids';

        navigate(pastBidsPath, {
          state: {
            bidSubmitted: true,
            message: t('requests.bidSuccessSubmitted'),
            activeTab: 'pending'
          }
        });
      }, 2500);
      
    } catch (err) {
      console.error('Error submitting bid:', err);
      setError(err.response?.data?.message || t('common.errors.submitBid'));
      setSubmitting(false);
    }
  };
  
  if (loading) {
    return <div className="loading">{t('common.loadingRequestDetails')}</div>;
  }
  
  if (!request) {
    return <div className="error-message">{t('common.requestNotFound')}</div>;
  }
  
  if (bidSubmitted) {
    return (
      <div className="card">
        <div className="alert-success" style={{ padding: '20px', margin: '20px 0' }}>
          <h3>{t('requests.bidSuccessSubmitted')}</h3>
        </div>
      </div>
    );
  }
  
  return (
    <div className="company-dashboard">
      
      {/* Navigation button outside and above the card */}
      <div className="page-navigation" style={{ marginBottom: '14px' }}>
        <button
          type="button"
          className="back-btn"
          onClick={() => navigate('/bank/open-requests')}
        >
          ← {t('bids.backToNewBids')}
        </button>
      
      </div>
      
      <div className="card">
       <div className="card-title">
        <Edit size={20} />
        <span>{t('requests.requestDetails')}</span>
        <div>
          <span className="status status-open">{t('status.open')}</span>
        </div>
      </div>
      
      <div className="request-info detail-list-lined">
        {/* Horizontal info box - same style as BidDetail */}
        <div className="allocation-details-card">
          <div style={{ display: 'flex', gap: '40px', justifyContent: 'center' }}>
            <div>
              <strong>{t('requests.requestId')}:</strong> #{request.requestId}
            </div>
            <div>
              <strong>{t('requests.company')}:</strong> {request.company?.name || t('bids.notAvailable')}
            </div>
            <div>
              <strong>{t('profile.vatNumber')}:</strong> {request.company?.vatNumber || t('profile.vatNumberNotSpecified')}
            </div>
            <div>
              <strong>{t('requests.datePosted')}:</strong> {new Date(request.createdAt).toLocaleDateString()}
            </div>
          </div>
        </div>

        <h3 style={{
          marginTop: '16px',
          ...(request.requestType === 'hot_money' && { color: '#e65100' })
        }}>
          {request.requestType === 'hot_money' ? t('requests.hotMoneyRequest') : t('requests.liquidityRequest')}
        </h3>

        {/* NEW: Currency */}        
        <div className="detail-item">
          <div className="detail-label">{t('requests.currency') || 'Currency'}:</div>
          <div className="detail-value">{request.currency || 'EUR'}</div>
        </div>

        <div className="detail-item">
          <div className="detail-label">{t('requests.totalAmountLabel')}:</div>
          <div className="detail-value">{formatCurrencyWithSymbol(request.amount, request.currency || 'EUR')}</div>
        </div>
        
        {/*Mostra i rate types accettati qui nelle info della richiesta */}
        <div className="detail-item">
          <div className="detail-label">{t('requests.availableRateTypes')}:</div>
          <div className="detail-value">
            {request.rateTypes && request.rateTypes.length > 0 
              ? request.rateTypes.map(type => 
                  type === 'floating' ? t('requests.floating') : t('requests.fixed')
                ).join(', ')
              : (request.rateType === 'floating' ? t('requests.floating') : t('requests.fixed'))
            }
          </div>
        </div>

        <div className="detail-item">
          <div className="detail-label">{t('requests.maturity')}:</div>
          <div className="detail-value">
            {request.duration === 'custom'
              ? formatDuration(request.duration, t)
              : formatDuration(request.duration, t, request.customStartDate, request.customEndDate)}
          </div>
        </div>
        {request.duration === 'custom' && request.customStartDate && (
          <div className="detail-item">
            <div className="detail-label">{t('requests.customStartDate')}:</div>
            <div className="detail-value">{new Date(request.customStartDate).toLocaleDateString()}</div>
          </div>
        )}
        {request.duration === 'custom' && request.customEndDate && (
          <div className="detail-item">
            <div className="detail-label">{t('requests.customEndDate')}:</div>
            <div className="detail-value">{new Date(request.customEndDate).toLocaleDateString()}</div>
          </div>
        )}

        {/* Withdrawal Type Display — nascosto per hot_money */}
        {request.requestType !== 'hot_money' && (
          <div className="detail-item">
            <div className="detail-label">{t('requests.withdrawalType')}:</div>
            <div className="detail-value">
              {getWithdrawalTypeDisplay(request.withdrawalType)}
            </div>
          </div>
        )}

        <div className="detail-item">
          <div className="detail-label">{t('requests.bidDeadline')}:</div>
          <div className="detail-value">
            {new Date(request.bidDeadline).toLocaleDateString()}
            {' '}
            <span style={{ color: '#6c757d' }}>
              {new Date(request.bidDeadline).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} CET
            </span>
          </div>
        </div>
        {/* Display company notes if available */}
        {request.notes && (
          <div className="detail-item">
          <div className="detail-label">{t('requests.companyNotes')}:</div>
          <div className="detail-value request-notes">{request.notes}</div>
          </div>
        )}
      </div>
              
      
      {/* Renewal banner */}
      {request.isRenewal && (
        <div style={{
          backgroundColor: '#fff3e0',
          color: '#e65100',
          border: '1px solid #ffb74d',
          borderLeft: '4px solid #e65100',
          padding: '12px 16px',
          borderRadius: '6px',
          marginTop: '12px',
          marginBottom: '16px'
        }}>
          <p style={{ margin: 0, fontWeight: 'bold' }}>{t('requests.renewalBannerMessage')}</p>
        </div>
      )}
      
      <div className="bid-form-container" style={{ marginTop: '36px' }}>
        <h3 style={{
          color: 'var(--primary)',
          fontSize: '20px',
          fontWeight: '700',
          marginBottom: '18px',
          textDecoration: 'underline'
        }}>
          {t('requests.submitYourBid')}
        </h3>
        
        {/* Display error message prominently above form */}
        {error && (
          <div className="alert-danger" style={{ 
            padding: '10px', 
            margin: '10px 0', 
            borderRadius: '4px', 
            backgroundColor: '#f8d7da', 
            color: '#721c24', 
            border: '1px solid #f5c6cb',
            fontWeight: 'bold'
          }}>
            {error}
          </div>
        )}
        
        <form id="bid-form" onSubmit={onSubmit} noValidate>
          <div className="form-group">
            <label htmlFor="amount">{t('bids.bidAmount')} ({request.currency || 'EUR'})</label>
            <input
              type="text"
              id="amount"
              name="amount"
              placeholder={t('requests.enterBidAmount')}
              value={formData.amount}
              onChange={onChange}
              required
              className={error && error.includes('amount') ? 'input-error' : ''}
            />
            {request.isRenewal && (
              <small style={{ display: 'block', marginTop: '4px' }}>
                {t('requests.renewalBannerNote')}
              </small>
            )}
          </div>

          {/* Rate Type Selection - Only show if multiple rate types are available */}
          {needsRateTypeSelection() && (
            <div className="form-group">
              <label htmlFor="rateType">{t('bids.selectRateType')} *</label>
              <select
                id="rateType"
                name="rateType"
                className="form-select-custom"
                value={formData.rateType}
                onChange={onChange}
                required
              >
                <option value="">{t('requests.selectRateType')}</option>
                {getAvailableRateTypes().map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <small>
                {t('requests.rateTypeSelectionDesc')}
              </small>
            </div>
          )}

          {/* Base Rate Selection - Show for floating rate */}
          {((formData.rateType === 'floating') || 
            (!needsRateTypeSelection() && request.rateTypes && request.rateTypes[0] === 'floating')) && (
            <div className="form-group">
              <label htmlFor="baseRate">{t('bids.baseRate')} *</label>
              <select
                id="baseRate"
                name="baseRate"
                className="form-select-custom"
                value={formData.baseRate}
                onChange={onChange}
                required
              >
                <option value="">{t('bids.selectBaseRate')}</option>
                {getBaseRateOptions(request.currency).map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <small>
                {t('bids.baseRateDescription')}
              </small>
            </div>
          )}

          {/* Update the interest rate field label to be dynamic */}
          <div className="form-group">
            <label htmlFor="interestRate">
              {needsRateTypeSelection() 
                ? (formData.rateType === 'floating' 
                    ? t('bids.interestRateFloating') 
                    : formData.rateType === 'fixed' 
                      ? t('bids.interestRateFixed') 
                      : t('bids.interestRate'))
                : getInterestRateLabel(request.rateTypes && request.rateTypes.length === 1 ? request.rateTypes[0] : request.rateType)
              }
            </label>
            <input
              type="text"
              id="interestRate"
              name="interestRate"
              placeholder={
                needsRateTypeSelection() 
                  ? (formData.rateType === 'floating' 
                      ? t('requests.enterSpread') 
                      : formData.rateType === 'fixed' 
                        ? t('requests.enterInterestRate')
                        : t('requests.selectRateTypeFirst'))
                  : (request.rateTypes && request.rateTypes.includes('floating') 
                      ? t('requests.enterSpread') 
                      : t('requests.enterInterestRate'))
              }
              value={formData.interestRate}
              onChange={onChange}
              required
              disabled={needsRateTypeSelection() && !formData.rateType}
              className={error && (error.includes('interest rate') || error.includes('spread')) ? 'input-error' : ''}
            />
          </div>

          <div className="form-group">
            <label htmlFor="dayCountBasis">{t('vatCredit.dayCountBasis')} *</label>
            <select
              id="dayCountBasis"
              name="dayCountBasis"
              className="form-select-custom"
              value={formData.dayCountBasis}
              onChange={onChange}
              required
            >
              {DAY_COUNT_BASES.map(basis => (
                <option key={basis} value={basis}>
                  {t(`vatCredit.dayCountBases.${basis}`)}
                </option>
              ))}
            </select>
          </div>

          {/* Notice Period for Early Withdrawal */}
          {request.withdrawalType === 'early_allowed' && (
            <div className="form-group">
              <label htmlFor="noticePeriod">{t('bids.noticePeriod')} ({t('common.days')})</label>
              <input
                type="number"
                id="noticePeriod"
                name="noticePeriod"
                placeholder={t('requests.enterNoticePeriod')}
                min="1"
                step="1"
                value={formData.noticePeriod}
                onChange={onChange}
                required
                className={error && error.includes('notice period') ? 'input-error' : ''}
              />
              <small>
                {t('requests.noticePeriodDesc')}
              </small>
            </div>
          )}

          {/* NEW: Penalty Fields for Early Withdrawal */}
          {request.withdrawalType === 'early_allowed' && (
            <>
              <div className="form-group">
                <label htmlFor="penaltyApplies">{t('bids.withdrawalPenalty')}</label>
                <select
                  id="penaltyApplies"
                  name="penaltyApplies"
                  value={formData.penaltyApplies}
                  onChange={onChange}
                  required
                  className={`form-select-custom ${error && error.includes('penalties apply') ? 'input-error' : ''}`}
                >
                  <option value="">{t('requests.selectPenaltyOption')}</option>
                  <option value="no">{t('common.no')}</option>
                  <option value="yes">{t('common.yes')}</option>
                </select>
                <small>
                  {t('requests.penaltyDesc')}
                </small>
              </div>

              {formData.penaltyApplies === 'yes' && (
                <div className="form-group">
                  <label htmlFor="penaltyDescription">{t('bids.penaltyTerms')}</label>
                  <textarea
                    id="penaltyDescription"
                    name="penaltyDescription"
                    rows="3"
                    placeholder={t('requests.enterPenaltyDesc')}
                    value={formData.penaltyDescription}
                    onChange={onChange}
                    required
                    className={error && error.includes('penalty description') ? 'input-error' : ''}
                  ></textarea>
                 </div>
              )}
            </>
          )}

          {/* Lock-in Period for Overnight 
          {request.duration === 'overnight' && (
            <div className="form-group">
              <label htmlFor="lockInPeriod">{t('bids.lockInPeriod')} (expiry date)</label>
              <input
                type="date"
                id="lockInPeriod"
                name="lockInPeriod"
                value={formData.lockInPeriod}
                onChange={onChange}
                required
                className={error && error.includes('lock-in period') ? 'input-error' : ''}
              />
              <small>
                {t('requests.lockInPeriodDesc')}
              </small>
            </div>
          )} */}     
          
          {/* Support Account Field - Only visible for NON-affiliated banks */}
          {!isAffiliatedBank && (
            <div className="form-group">
              <label htmlFor="supportAccount">{t('bids.supportAccount')} *</label>
              <select
                id="supportAccount"
                name="supportAccount"
                value={formData.supportAccount}
                onChange={onChange}
                required
                className={`form-select-custom ${error && error.includes('support account') ? 'input-error' : ''}`}
              >
                <option value="">{t('requests.selectSupportAccountOption')}</option>
                <option value="yes">{t('common.yes')}</option>
                <option value="no">{t('common.no')}</option>
              </select>
              <small>
                {t('requests.supportAccountDesc')}
              </small>
            </div>
          )}

          <div className="form-group">
            <label htmlFor="notes">{t('requests.additionalNotes')}</label>
            <textarea
              id="notes"
              name="notes"
              rows="4"
              placeholder={t('requests.additionalBidInfo')}
              value={formData.notes}
              onChange={onChange}
            ></textarea>
          </div>
          
          <div className="form-actions">
            <button
              type="button"
              className="btn btn-light"
              onClick={() => navigate('/bank/open-requests')}
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={submitting}
            >
              {submitting ? t('actions.submitting') : t('bids.submitBid')}
            </button>
          </div>
        </form>
      </div>
    </div>
    </div>
  );
};

export default SubmitBid;